/**
 * AI Auto-Processing Card Component
 *
 * Informational card reflecting the ACTUAL posting policy enforced by the
 * pipeline (core/ai/posting_policy.py):
 * - 80%+ confidence: auto-posted to the books (platform compliance floor,
 *   cannot be lowered)
 * - Below 80%: always routed to the "Review Needed" tab for approval
 * The old medium-band toggle was removed (iter1256): the 0.80 floor is
 * non-negotiable in the statement pipeline, so the toggle never changed
 * what actually posted.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Sparkles,
  CheckCircle2,
  Clock,
  ShieldCheck
} from 'lucide-react';

export const AISettingsCard = ({ className = '' }) => {
  return (
    <Card className={`border-2 border-[#108981]/20 ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#108981]" />
          AI Auto-Processing
        </CardTitle>
        <CardDescription>
          How the AI decides what gets posted to your books
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* High Confidence - auto-posted */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="ai-policy-high-confidence">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-emerald-900">High Confidence (80% and above)</p>
              <p className="text-sm text-emerald-700">Auto-posted to your books — visible in the &quot;In Books&quot; tab</p>
            </div>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700">Auto-Post</Badge>
        </div>

        {/* Below threshold - always reviewed */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="ai-policy-below-threshold">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-900">Below 80%</p>
              <p className="text-sm text-amber-700">Always waits for your approval in the &quot;Review Needed&quot; tab</p>
            </div>
          </div>
          <Badge variant="outline" className="text-amber-700 border-amber-300">Your Review</Badge>
        </div>

        {/* Compliance note */}
        <div className="flex items-start gap-2 px-1" data-testid="ai-policy-floor-note">
          <ShieldCheck className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            The 80% threshold is a platform-wide integrity floor and cannot be
            lowered — nothing is ever posted to your ledger below it.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AISettingsCard;
